import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {PGPApi} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const pgpApi = new PGPApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface PGPKeyRingImportDialogProps {
    onClose : OnClose
}

export const PGPKeyRingImportDialog = ({onClose} : PGPKeyRingImportDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The files to import
     */
    const [file, setFile] = useState<File>();
    /**
     * If true, keys which cannot be imported are ignored instead of failing the complete import
     */
    const [ignoreParsingErrors, setIgnoreParsingErrors] = useState<boolean>(false);
    /**
     * The import key store password
     */
    const [keyRingPassword, setKeyRingPassword] = useState<string>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while importing to show a spinner on the import button
     */
    const [importing, setImporting] = useState<boolean>(false);
    /**
     * Reference to the file input element
     */
    const fileInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const importKeyRing = () =>
    {
        setImporting(true);

        pgpApi.pGPControllerImportKeyring(ignoreParsingErrors, keyRingPassword, file)
            .then(() => {
                onClose(DialogCloseCause.Succes);
            })
        .catch((err: Error | AxiosError) => {
            logger.error("Error importing certificates", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setImporting(false);
        });
    }

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true}
               onHide={() => onHideDialog()}
               onEntered={() => fileInputRef.current?.focus()}
               backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("pgp.import-keyring")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <Form.Group className="mb-3">
                    <Form.Label>{t("pgp.keyring-file")}</Form.Label>
                    <Form.Control
                        size="sm"
                        type="file"
                        accept=""
                        ref={fileInputRef}
                        onChange={e => setFile(((e.target as HTMLInputElement).files as FileList)[0])}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>{t("pgp.secret-key-password")}</Form.Label>
                    <Form.Control type="password"
                                  placeholder={t("pgp.password")}
                                  onChange={e => setKeyRingPassword(e.target.value)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Check
                        type="checkbox"
                        checked={ignoreParsingErrors}
                        onChange={(e) => setIgnoreParsingErrors(e.target.checked)}
                        label={t("pgp.ignore-parsing-errors")}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={importKeyRing}
                        disabled={file === undefined}>
                    {importing && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {importing ? t("common.importing") : t("common.import")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
